import { useState } from 'react';
import type { TicketLink } from '../../api/types';
import { looksLikeSvg, parseSafeUrl } from '../../lib/safe-url';

const KIND_LABEL: Record<TicketLink['kind'], string> = {
  image: 'Image',
  drive: 'Google Drive',
  kg: 'Knowledge graph',
  url: 'Link',
};

/**
 * 🔴 USER-SUPPLIED URLS. db/007 stores links only — no uploads, no proxying — and its own note spells
 * out the renderer's obligation, so it is restated here where the rendering happens:
 *
 *   ✅ `kind='image'` → an <img src>, and nothing else.
 *   ⛔ NEVER inline <svg>, <object>, <embed>, <iframe>, or a CSS background-image.
 *   ⛔ NEVER an href whose scheme is not http/https — `javascript:` is the famous one, but `data:`
 *      carries an HTML document and `blob:` carries a script.
 *   ✅ `referrerPolicy="no-referrer"` on the <img>: without it the request leaks the PlanHub URL, and
 *      therefore which ticket was being read, to whoever hosts the image.
 *   ✅ `rel="noopener noreferrer nofollow"` on every anchor.
 *
 * ⛔ And it must not be "improved" later by fetching the URL server-side to proxy it — that is an
 * SSRF, not a convenience (db/007 says the same).
 */
export function LinkList({ links, compact = false }: { links: TicketLink[]; compact?: boolean }) {
  if (links.length === 0) return null;

  const images = links.filter((link) => link.kind === 'image');
  const documents = links.filter((link) => link.kind !== 'image');

  return (
    <div className={compact ? 'mt-2 space-y-2' : 'space-y-3'}>
      {images.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {images.map((link) => (
            <li key={link.id}>
              <LinkedImage link={link} />
            </li>
          ))}
        </ul>
      ) : null}

      {documents.length > 0 ? (
        <ul className="space-y-1">
          {documents.map((link) => (
            <li key={link.id}>
              <SafeAnchor link={link} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function SafeAnchor({ link }: { link: TicketLink }) {
  const safe = parseSafeUrl(link.url);
  if (!safe.ok) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-[color:var(--color-ink-muted)]">
        <span aria-hidden="true">⛔</span>
        <span>
          {link.title ?? KIND_LABEL[link.kind]} — link not shown ({safe.reason})
        </span>
      </span>
    );
  }
  return (
    <a
      href={safe.href}
      target="_blank"
      rel="noopener noreferrer nofollow"
      referrerPolicy="strict-origin-when-cross-origin"
      className="inline-flex max-w-full items-baseline gap-1.5 text-sm text-[color:var(--color-accent)] hover:underline"
    >
      <span className="shrink-0 text-[10px] tracking-wide text-[color:var(--color-ink-subtle)] uppercase">
        {KIND_LABEL[link.kind]}
      </span>
      <span className="truncate">{link.title ?? safe.href}</span>
      <span aria-hidden="true" className="shrink-0 text-xs">
        ↗
      </span>
    </a>
  );
}

function LinkedImage({ link }: { link: TicketLink }) {
  const [failed, setFailed] = useState(false);
  const safe = parseSafeUrl(link.url);

  if (!safe.ok) {
    return (
      <span className="inline-block rounded border border-dashed border-[color:var(--color-line)] px-2 py-1.5 text-xs text-[color:var(--color-ink-muted)]">
        ⛔ Image not shown — {safe.reason}
      </span>
    );
  }

  if (failed) {
    return (
      <span className="inline-block rounded border border-dashed border-[color:var(--color-line)] px-2 py-1.5 text-xs text-[color:var(--color-ink-muted)]">
        Image could not be loaded from {safe.host} — <SafeAnchor link={link} />
      </span>
    );
  }

  return (
    <a
      href={safe.href}
      target="_blank"
      rel="noopener noreferrer nofollow"
      title={link.title ?? safe.href}
      className="block"
    >
      <img
        src={safe.href}
        alt={link.title ?? `Image attached to this ticket (${safe.host})`}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        className="max-h-40 max-w-[12rem] rounded border border-[color:var(--color-line)] object-contain"
      />
      {looksLikeSvg(safe.href) ? (
        <span className="mt-0.5 block text-[10px] text-[color:var(--color-ink-subtle)]">
          SVG — rendered as an image, never inlined
        </span>
      ) : null}
    </a>
  );
}
